import { useRouter } from "next/navigation";
import { useCallback, useRef, useState } from "react";
import { type GameApp } from "@/constants/apps";
import { useSoundEffects } from "@thehoneyjar/sound-engine";

export const useHoveredApp = () => {
  const [hoveredApp, setHoveredApp] = useState<GameApp | null>(null);
  const handleHover = useCallback((app: GameApp | null) => setHoveredApp(app), []);
  return { hoveredApp, handleHover };
};

export const useAppNavigation = (onBuyModalOpen?: () => void) => {
  const router = useRouter();
  const { playSound } = useSoundEffects();
  const clickGuard = useRef(false);

  const navigate = useCallback(
    (app: GameApp) => {
      if (clickGuard.current) return;
      clickGuard.current = true;
      // TODO(audio-register): consider isRouteHibernating gate — see constants/soundtrack.ts
      void playSound("buttonClick");
      if (app.path === "/buy") {
        onBuyModalOpen?.();
        clickGuard.current = false;
        return;
      }
      router.push(app.path);
      setTimeout(() => {
        clickGuard.current = false;
      }, 500);
    },
    [router, onBuyModalOpen, playSound],
  );

  const prefetch = useCallback(
    (path: string) => {
      try {
        // Only prefetch internal app routes
        if (path?.startsWith("/")) {
          // next/navigation router supports prefetch in App Router; optional call
          // Older versions may not have it — cast to optional shape locally.
          const r = router as unknown as { prefetch?: (href: string) => void };
          r.prefetch?.(path);
        }
      } catch (_) {
        // ignore prefetch errors
      }
    },
    [router],
  );

  return { navigate, prefetch };
};

// Lightweight facade to satisfy components expecting a consolidated arcade state
export const useArcadeState = () => {
  const { navigate, prefetch } = useAppNavigation();
  const [hoveredApp, setHoveredApp] = useState<string | null>(null);
  const [buyModalOpen, setBuyModalOpen] = useState(false);
  const [floatingTexts, setFloatingTexts] = useState<
    { id: string; x: number; y: number; font: string; delay: number }[]
  >([]);

  const onHoverEnter = useCallback((id: string) => setHoveredApp(id), []);
  const onHoverLeave = useCallback(() => setHoveredApp(null), []);

  const handleAppClick = useCallback(
    (app: GameApp) => {
      if (app.path === "/buy") {
        setBuyModalOpen(true);
        return;
      }
      navigate(app);
    },
    [navigate],
  );

  const addFloatingText = useCallback((x: number, y: number) => {
    const id = Math.random().toString(36).slice(2);
    setFloatingTexts((prev) => [...prev, { id, x, y, font: "font-pressStart2P", delay: 0 }]);
    // Auto-remove after animation duration
    setTimeout(() => {
      setFloatingTexts((prev) => prev.filter((t) => t.id !== id));
    }, 1200);
  }, []);

  return {
    hoveredApp,
    buyModalOpen,
    floatingTexts,
    onHoverEnter,
    onHoverLeave,
    handleAppClick,
    setBuyModalOpen,
    addFloatingText,
    prefetchPath: prefetch,
  } as const;
};
